import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  ViewChild,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import {
  MatTree,
  MatTreeNode,
  MatTreeNodeDef,
  MatTreeNodePadding,
  MatTreeNodeToggle,
} from '@angular/material/tree';
import { IRouteConfig } from '../../../core/interfaces/IRouteConfig.interface';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { DialogService } from '../../../services/dialog.service';
import { MenuTreeService } from '../../../services/menu-tree.service';

@Component({
  selector: 'app-menu-tree',
  imports: [
    MatIcon,
    MatIconButton,
    MatTree,
    MatTreeNode,
    MatTreeNodeDef,
    MatTreeNodePadding,
    MatTreeNodeToggle,
    RouterLink,
    RouterLinkActive,
  ],
  templateUrl: './menu-tree.component.html',
  styleUrl: './menu-tree.component.scss',
})
export class MenuTreeComponent implements AfterViewInit {
  private readonly _dialogService = inject(DialogService);
  private readonly _menuTreeService = inject(MenuTreeService);
  private readonly _elementRef: ElementRef = inject(ElementRef);
  private menuWrapper!: HTMLElement;
  private isScrolling = false;
  @ViewChild('menuTree') menuTree!: MatTree<IRouteConfig>;

  protected dataSource = computed(() => {
    return this._menuTreeService.filteredMenu();
  });

  protected childrenAccessor = (node: IRouteConfig) =>
    node.children
      ? Object.values(node.children).filter(
          (child) => child.name && child.placeInMenuTree && child.enabled,
        )
      : [];
  protected hasChild = (_: number, node: IRouteConfig) =>
    !!node.children &&
    Object.values(node.children).filter(
      (child) => child.name && child.placeInMenuTree && child.enabled,
    ).length > 0;

  constructor() {
    effect(() => {
      this.dataSource();

      const scrollbox =
        this._elementRef.nativeElement.querySelector('.menu-container');
      if (scrollbox) this.setShadows({ target: scrollbox } as Event);
    });
  }

  ngAfterViewInit() {
    this.menuWrapper =
      this._elementRef.nativeElement.querySelector('.menu-wrapper');
    const scrollbox =
      this._elementRef.nativeElement.querySelector('.menu-container');
    scrollbox.addEventListener('scroll', (e: Event) => this.setShadows(e));

    this.menuTree._getExpansionModel().changed.subscribe(() => {
      this.setShadows({ target: scrollbox } as Event);
    });
  }

  public getRoutePath(routerLink: string): string {
    return routerLink.split('?')[0];
  }

  public getRouteQueryParams(
    routerLink: string,
  ): Record<string, string> | null {
    const parts = routerLink.split('?');
    if (parts.length < 2) return null;

    const params: Record<string, string> = {};
    const searchParams = new URLSearchParams(parts[1]);
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  }

  private setShadows(event: Event) {
    if (!this.isScrolling) {
      requestAnimationFrame(() => {
        const target = event.target as HTMLElement;

        if (target.scrollTop > 0) {
          this.menuWrapper.classList.add('off-top');
        } else {
          this.menuWrapper.classList.remove('off-top');
        }
        const hasMoreContent = target.scrollHeight > target.clientHeight;
        const notAtBottom =
          target.scrollTop < target.scrollHeight - target.clientHeight;

        if (hasMoreContent && notAtBottom) {
          this.menuWrapper.classList.add('off-bottom');
        } else {
          this.menuWrapper.classList.remove('off-bottom');
        }

        this.isScrolling = false;
      });
      this.isScrolling = true;
    }
  }

  /**
   * Opens a Material Dialog based on the provided dialog key.
   * The dialog component is retrieved from DIALOG_COMPONENT_MAP.
   *
   * @param dialogKey - Unique identifier for the dialog (e.g., 'e-logbuch')
   * @example
   * openDialog('e-logbuch'); // Opens ELogBuchComponent in a dialog
   */
  openDialog(dialogKey: string) {
    this._dialogService.openDialog(dialogKey);
  }
}
